import React from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  contentPreviewUrl: string | null;
  contentFiles: File[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

const UploadIcon: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
    <Box sx={{ width: '62px', height: '55px', borderLeft: '40px solid transparent', borderRight: '40px solid transparent', borderBottom: `35px solid ${color}` }} />
    <Box sx={{ width: '35px', height: '25px', backgroundColor: color, mb: '5px' }} />
    <Box sx={{ width: '80px', height: '11.5px', backgroundColor: color, borderRadius: '2px' }} />
  </Box>
);

const ContentUploadZone: React.FC<Props> = ({ contentPreviewUrl, contentFiles, inputRef, onUpload, onRemove }) => (
  <Box
    sx={{
      position: 'relative',
      width: '637px',
      height: '396px',
      border: '2px dashed #F39A81',
      borderRadius: '30px',
      padding: contentPreviewUrl ? 0 : 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      '&:hover': {
        backgroundColor: contentPreviewUrl ? 'transparent' : '#fef5f3',
        borderColor: '#e88972',
      },
      '&:hover .video-remove-btn': {
        opacity: contentPreviewUrl ? 1 : 0,
      },
    }}
    component="label"
  >
    <input ref={inputRef} type="file" hidden multiple accept="video/*" onChange={onUpload} />

    {contentPreviewUrl ? (
      <>
        <video
          src={contentPreviewUrl}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '28px' }}
        >
          <track kind="captions" />
        </video>

        {/* Remove button */}
        <Box
          className="video-remove-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
          sx={{
            position: 'absolute', top: 8, right: 8, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', opacity: 0,
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            zIndex: 10,
            '&:hover': { transform: 'scale(1.2)' },
          }}
        >
          <Typography sx={{ fontSize: '28px', fontWeight: 'bold', color: '#fff', lineHeight: 1, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>×</Typography>
        </Box>

        {/* Label overlay */}
        <Box
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            padding: '40px 16px 16px', borderRadius: '0 0 28px 28px',
          }}
        >
          <Typography variant="h5" sx={{ fontFamily: 'Maven Pro', fontWeight: 400, color: '#fff', fontSize: '20px', textAlign: 'center' }}>
            Content
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#fff', fontSize: '14px', textAlign: 'center' }}>
            {contentFiles[0]?.name}
          </Typography>
        </Box>
      </>
    ) : (
      <>
        <UploadIcon color="#F39A81" />
        <Typography variant="h5" sx={{ fontFamily: 'Maven Pro', fontWeight: 400, color: '#000', fontSize: '20px', mt: 1 }}>
          Content
        </Typography>
      </>
    )}
  </Box>
);

export default ContentUploadZone;
